import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { PackageCheck, MapPin, CheckCircle2, Truck, ChevronDown, Zap } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import Timeline from "../components/Timeline";

export default function Accettazione() {
  const { text } = useSettings();
  const [items, setItems] = useState([]);
  const [checks, setChecks] = useState({});
  const [open, setOpen] = useState({});
  const [urg, setUrg] = useState({ articolo: "", message: "" });

  const setCheck = (id, field, val) => setChecks((c) => ({ ...c, [id]: { ...c[id], [field]: val } }));

  const load = async () => {
    const { data } = await api.get("/acquisti");
    setItems(data.filter((a) => a.status === "ritirato" || a.status === "arrivato"));
  };

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, []);

  const markArrivato = async (a) => {
    const c = checks[a.id] || {};
    try {
      await api.put(`/acquisti/${a.id}/status`, {
        status: "arrivato",
        quantita_ricevuta: c.quantita_ricevuta ? parseFloat(c.quantita_ricevuta) : a.colli,
        note_accettazione: c.note || "",
      });
      toast.success("Merce accettata al banco!");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const inArrivo = items.filter((a) => a.status === "ritirato");
  const arrivati = items.filter((a) => a.status === "arrivato");

  const richiediUrgente = async () => {
    if (!urg.articolo.trim()) { toast.error("Indica l'articolo richiesto"); return; }
    try {
      await api.post("/alerts", { kind: "richiesta_urgente", articolo: urg.articolo, message: urg.message });
      toast.warning("Richiesta urgente inviata a buyer e picker");
      setUrg({ articolo: "", message: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("accettazione_title")}</h1>
      <p className="text-slate-400 mb-6">{text("accettazione_subtitle")}</p>

      <div className="bg-red-500/5 border border-red-500/30 rounded-md p-4 mb-6" data-testid="urgente-panel">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-5 h-5 text-red-400" />
          <span className="font-semibold text-red-300">Richiedi articolo URGENTE</span>
          <span className="text-xs text-slate-500">— avvisa subito buyer e picker (suono + notifica)</span>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input data-testid="urgente-articolo" value={urg.articolo} onChange={(e) => setUrg({ ...urg, articolo: e.target.value })}
            placeholder="Articolo richiesto (es. Basilico)" className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-red-400" />
          <input data-testid="urgente-note" value={urg.message} onChange={(e) => setUrg({ ...urg, message: e.target.value })}
            placeholder="Note (es. serve entro un'ora)" className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-red-400" />
          <button data-testid="urgente-invia" onClick={richiediUrgente}
            className="bg-red-500 text-white font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:bg-red-400 transition-colors">
            <Zap className="w-4 h-4" /> Invia urgenza
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">In arrivo ({inArrivo.length})</h2>
          <div className="space-y-3" data-testid="accettazione-todo">
            {inArrivo.length === 0 && <p className="text-slate-500 text-sm">Niente in arrivo al momento.</p>}
            {inArrivo.map((a) => (
              <div key={a.id} data-testid={`arrivo-${a.id}`} className="bg-slate-900/50 border border-blue-500/30 rounded-md p-4">
                <div className="flex items-start gap-3">
                  <Truck className={`w-6 h-6 shrink-0 ${a.origine === "fuori_mercato" ? "text-amber-400" : "text-blue-400"}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-100">{a.prodotto}</span>
                      <span className="font-mono-plex text-slate-400 text-sm">{a.colli} {a.unita}</span>
                      {a.origine === "fuori_mercato" && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">Fuori mercato</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-400 mt-0.5">
                      <MapPin className="w-4 h-4" />
                      {a.origine === "fuori_mercato"
                        ? (a.fornitore?.nome || "Fornitore esterno")
                        : `${a.posteggio?.nome || "—"}${a.picker?.name ? ` · ${a.picker.name}` : ""}`}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Q.tà ricevuta (attesa: {a.colli})</label>
                    <input
                      data-testid={`qta-ricevuta-${a.id}`}
                      type="number"
                      step="any"
                      placeholder={`${a.colli}`}
                      value={checks[a.id]?.quantita_ricevuta ?? ""}
                      onChange={(e) => setCheck(a.id, "quantita_ricevuta", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Note</label>
                    <input
                      data-testid={`note-arrivo-${a.id}`}
                      placeholder="es. 2 cassette mancanti"
                      value={checks[a.id]?.note ?? ""}
                      onChange={(e) => setCheck(a.id, "note", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                <button
                  data-testid={`arrivo-done-${a.id}`}
                  onClick={() => markArrivato(a)}
                  className="mt-3 w-full min-h-[48px] bg-emerald-500 text-slate-950 font-bold rounded-md flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5" /> Conferma ARRIVO
                </button>
                <button data-testid={`arrivo-dettagli-${a.id}`} onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                  className="mt-3 text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open[a.id] ? "rotate-180" : ""}`} /> Stato del processo
                </button>
                {open[a.id] && <div className="mt-3 border-t border-slate-800 pt-3"><Timeline a={a} compact /></div>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Arrivati oggi ({arrivati.length})</h2>
          <div className="space-y-3" data-testid="accettazione-done">
            {arrivati.map((a) => (
              <div key={a.id} className="bg-slate-900/50 border border-emerald-500/20 rounded-md p-4">
                <div className="flex items-center gap-3">
                  <PackageCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-100">{a.prodotto}</span>
                    <span className="font-mono-plex text-slate-400 text-sm ml-2">{a.colli} {a.unita}</span>
                    {a.quantita_ricevuta != null && (
                      <span className="font-mono-plex text-emerald-400 text-sm ml-2">ricevuti: {a.quantita_ricevuta}</span>
                    )}
                    <div className="text-sm text-slate-500">{a.posteggio?.nome}{a.note_accettazione ? ` · ${a.note_accettazione}` : ""}</div>
                  </div>
                </div>
                <button data-testid={`arrivo-dettagli-${a.id}`} onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                  className="mt-2 text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open[a.id] ? "rotate-180" : ""}`} /> Stato del processo
                </button>
                {open[a.id] && <div className="mt-3 border-t border-slate-800 pt-3"><Timeline a={a} compact /></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
